<template>
	<v-card flat class="pa-3">
		<div class="text-xs-left">
			<div v-if="!employeeCertifications.length && loggedInEmployeeIsAdminOrManager && !readOnly">
				<v-subheader>{{ isMe ? 'You do not currently have any certifications' : 'This employee does not have any certifications' }}</v-subheader>
				<v-btn color="primary" @click="openCertificationUploader"><v-icon class="mr-2">add</v-icon> Add Certification</v-btn>
			</div>
			<div v-else-if="loggedInEmployeeIsAdminOrManager  && !readOnly" style="display: flex; justify-content: end;">
				<v-btn color="primary" @click="openCertificationUploader"><v-icon class="mr-2">add</v-icon> Add Certification</v-btn>
			</div>

			<v-alert v-if="alertMessage" type="warning" color="warning" class="mb-3">{{ alertMessage }}</v-alert>

			<template v-if="employeeUploadReadyCerts.length">
				<v-label>Expired / Expiring Soon</v-label>
				<div class="certs-container">
					<v-sheet v-for="certification in employeeUploadReadyCerts" :key="certification.id" color="white" :class="certificationHasExpired(certification) ? 'expired-sheet' : 'expiring-sheet'" class="cert-sheet">
						<v-icon :color="certificationHasExpired(certification) ? 'error' : 'warning'">
							{{ certificationHasExpired(certification) ? 'mdi-alert-circle' : 'mdi-clock-alert' }}
						</v-icon>
						<div>
							<h4>
								<a href="#" @click.prevent.stop="handleOpenCertificationClick(certification)">{{ certification.name }}</a>
							</h4>
							<div class="caption">
								{{ certificationHasExpired(certification) ? 'Expired' : 'Expiring' }}
								<span class="red--text text--darken-1">{{ moment(certification.expiryDate).fromNow() }}</span>
							</div>
						</div>
						<v-spacer />
						<v-btn v-if="!readOnly" color="error" small @click="handleUploadClick(certification)">Update</v-btn>
						<v-menu v-if="!readOnly" offset-y :close-on-content-click="false">
							<template v-slot:activator="{ on, attrs }">
								<v-btn icon v-bind="attrs" v-on="on" class="mx-0">
									<v-icon>mdi-dots-horizontal</v-icon>
								</v-btn>
							</template>
							<certification-action-menu :certification="certification" />
						</v-menu>
					</v-sheet>
				</div>
			</template>

			<template v-if="validCertifications.length && !expiredOnly">
				<v-divider class="my-4"></v-divider>
				<v-label>Current</v-label>
				<div class="certs-container">
					<v-sheet v-for="certification in validCertifications" :key="certification.id" class="cert-sheet current-sheet">
						<v-icon color="indigo">mdi-certificate-outline</v-icon>
						<div>
							<h4>
								<a href="#" @click.prevent.stop="handleOpenCertificationClick(certification)">{{ certification.name }}</a>
							</h4>
							<div v-if="hasExpiryDate(certification)" class="caption">Expires {{ moment(certification.expiryDate).fromNow() }}</div>
							<div v-else-if="canExpire(certification)" class="caption grey--text darken-2">No Expiry Set</div>
							<div v-else class="caption grey--text darken-2">Does Not Expire</div>
						</div>
						<v-spacer />
						<v-menu v-if="!readOnly" offset-y :close-on-content-click="false">
							<template v-slot:activator="{ on, attrs }">
								<v-btn icon v-bind="attrs" v-on="on" class="mx-0">
									<v-icon>mdi-dots-horizontal</v-icon>
								</v-btn>
							</template>
							<certification-action-menu :certification="certification" />
						</v-menu>
					</v-sheet>
				</div>
			</template>

			<certification-action-menu-handlers />

			<certification-uploader v-model="isShowingCertUploadDialog" :employee-id="employeeId" :certification-type-id="editingCertTypeId" @onCertificationAdded="onCertificationAdded" />
			<certification-dialog v-if="showCertificationDialog" :certification="editingCertification" @closeDialog="showCertificationDialog = false" @onCertificationUpdated="onCertificationUpdated" />
		</div>
	</v-card>
</template>

<script>
	import { mapActions, mapGetters } from 'vuex';
	import moment from 'moment';
	import CertificationUploader from '@components/certifications/CertificationUploader.vue';
	import CertificationVersionHistory from '@components/certifications/CertificationVersionHistory.vue';
	import CertificationActionMenu from '@components/certifications/CertificationActionMenu.vue';
	import CertificationActionMenuHandlers from '@components/certifications/CertificationActionMenuHandlers.vue';
	import CertificationDialog from '@components/certifications/CertificationDialog.vue';

	export default {
		name: 'CertificationForEmployee',
		components: { CertificationActionMenuHandlers, CertificationActionMenu, CertificationVersionHistory, CertificationUploader, CertificationDialog },
		props: {
			employeeId: { type: String, required: true },
			certificationId: { type: String, default: null },
			readOnly: { type: Boolean, default: false },
			expiredOnly: { type: Boolean, default: false }
		},
		data() {
			return {
				isShowingCertUploadDialog: false,
				editingCertTypeId: undefined,
				showCertificationDialog: false,
				editingCertification: undefined
			};
		},
		computed: {
			...mapGetters({
				certifications: 'certifications/allCertifications',
				expiredCertifications: 'certifications/expired',
				expiringSoonCertifications: 'certifications/expiringSoon',
				certificationDownloading: 'certifications/isDownloading',
				loggedInEmployeeIsAdminOrManager: 'loggedInEmployeeIsAdminOrManager',
				certificationIsDeleted: 'certifications/isDeleted',
				getCertById: 'certifications/getCertificationById',
				loggedInEmployee: 'loggedInEmployee'
			}),
			employeeCertifications() {
				return this.certifications.filter(x => x.employeeId === this.employeeId && !x.deleted);
			},
			employeeExpiredCerts() {
				return this.expiredCertifications.filter(x => x.employeeId === this.employeeId);
			},
			employeeExpiringSoonCerts() {
				return this.expiringSoonCertifications.filter(x => x.employeeId === this.employeeId);
			},
			employeeUploadReadyCerts() {
				return [...this.employeeExpiredCerts, ...this.employeeExpiringSoonCerts];
			},
			validCertifications() {
				return this.employeeCertifications.filter(x => !this.employeeUploadReadyCerts.find(c => c.id === x.id));
			},
			alertMessage() {
				if (this.employeeExpiredCerts.length && this.employeeExpiringSoonCerts.length) {
					return `You have ${this.employeeExpiredCerts.length} expired and ${this.employeeExpiringSoonCerts.length} expiring soon.`;
				}
				if (this.employeeExpiredCerts.length) {
					return `You have ${this.employeeExpiredCerts.length} expired certification(s).`;
				}
				if (this.employeeExpiringSoonCerts.length) {
					return `You have ${this.employeeExpiringSoonCerts.length} certification(s) expiring soon.`;
				}
				return '';
			},
			isMe() {
				return this.loggedInEmployee.id === this.employeeId
			}
		},
		watch: {
			certificationId: {
				immediate: true,
				handler(val) {
					if (val) {
						const cert = this.getCertById(val);
						this.editingCertTypeId = cert.certificationTypeId;
						this.isShowingCertUploadDialog = true;
					}
				}
			}
		},
		methods: {
			moment,
			...mapActions({
				initializeCertifications: 'certifications/initialize',
				initializeCertificationTypes: 'certificationTypes/initialize',
				initializeEmployees: 'employees/initialize',
				downloadCertification: 'certifications/download',
				deleteCertification: 'certifications/delete',
				sendReminder: 'certifications/sendReminder'
			}),
			certificationHasExpired(cert) {
				return this.employeeExpiredCerts.some(x => x.id === cert.id);
			},
			handleUploadClick(cert) {
				this.editingCertTypeId = cert.certificationTypeId;
				this.isShowingCertUploadDialog = true;
			},
			hasExpiryDate(cert) {
				return !!cert.expiryDate;
			},
			openCertificationUploader() {
				this.editingCertTypeId = undefined;
				this.isShowingCertUploadDialog = true;
			},
			handleOpenCertificationClick(cert) {
				this.showCertificationDialog = true;
				this.editingCertification = cert;
			},
			canExpire(cert) {
				return cert.typeConfiguration.hasExpiry;
			},
			onCertificationAdded(certification) {
				this.showCertificationDialog = true;
				this.editingCertification = certification;
			},
			onCertificationUpdated(updatedCertification) {
				this.editingCertification = updatedCertification;
			}
		},
		async created() {
			await Promise.all([this.initializeCertifications(), this.initializeEmployees(), this.initializeCertificationTypes()]);
		}
	};
</script>

<style scoped>
	.certs-container {
		margin-top: 1rem;
		display: flex;
		flex-wrap: wrap;
		gap: 1rem;
	}

	.cert-sheet {
		display: flex;
		box-shadow: 0 3px 1px -2px rgba(0, 0, 0, 0.2), 0 2px 2px 0 rgba(0, 0, 0, 0.14), 0 1px 5px 0 rgba(0, 0, 0, 0.12);
		padding: 1.5rem 1rem;
		gap: 1rem;
		min-width: 200px;
	}

	.expired-sheet {
		background-color: #ffeceb !important;
		border-left: 4px solid #ff5252 !important;
	}

	.expiring-sheet {
		background-color: #fff8e1 !important;
		border-left: 4px solid #ffa000 !important;
	}

	.current-sheet {
		/* border-left: 4px solid #3f51b5 !important; */
	}
</style>
